import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { QrCode, Printer } from 'lucide-react';
import { escapeHtml, openHtmlWindow } from '@/lib/letterhead';

export default function QRCodeOspite({ ospite, size = 'sm' }) {
  const [showDialog, setShowDialog] = useState(false);
  const printRef = useRef(null);

  if (!ospite) return null;

  const baseUrl = window.location.origin;
  const qrData = `${baseUrl}/ospiti/${ospite.id}`;

  const handlePrintQR = () => {
    const content = printRef.current;
    if (!content) return;
    const e = escapeHtml;
    openHtmlWindow(`<!DOCTYPE html><html><head><title>QR Code - ${e(ospite.cognome)} ${e(ospite.nome)}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; }
      @media print { @page { size: 62mm 100mm; margin: 2mm; } }
      .label { width: 58mm; padding: 3mm; border: 1px dashed #ccc; display: flex; flex-direction: column; align-items: center; gap: 2mm; }
      .label-header { font-size: 7pt; color: #666; text-transform: uppercase; letter-spacing: 1px; text-align: center; }
      .label-name { font-size: 12pt; font-weight: bold; text-align: center; line-height: 1.2; }
      .label-info { font-size: 8pt; color: #444; text-align: center; }
      .label-id { font-size: 6pt; color: #999; font-family: monospace; }
    </style></head><body>
    <div class="label">
      <div class="label-header">RSA S. Maria Valleverde</div>
      ${content.querySelector('.qr-svg-print').outerHTML}
      <div class="label-name">${e(ospite.cognome)} ${e(ospite.nome)}</div>
      <div class="label-info">Camera ${e(ospite.stanza || '-')} / Letto ${e(ospite.letto || '-')}${ospite.area ? ' | ' + (ospite.area === 'verde' ? 'Area Verde' : 'Area Gialla') : ''}</div>
      ${ospite.data_nascita ? `<div class="label-info">Nato/a: ${e(new Date(ospite.data_nascita).toLocaleDateString('it-IT'))}</div>` : ''}
      <div class="label-id">ID: ${e(ospite.id.substring(0, 8))}</div>
    </div>
    <script>setTimeout(()=>{window.print();window.close()},400)</script></body></html>`, 'width=400,height=600');
  };

  const handlePrintBraccialetto = () => {
    const content = printRef.current;
    if (!content) return;
    const e = escapeHtml;
    openHtmlWindow(`<!DOCTYPE html><html><head><title>Braccialetto - ${e(ospite.cognome)} ${e(ospite.nome)}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; }
      @media print { @page { size: 25mm 70mm; margin: 1mm; } }
      .wristband { width: 68mm; padding: 2mm 3mm; display: flex; align-items: center; gap: 2mm; border: 1px dashed #ccc; }
      .wristband-text { flex: 1; }
      .wristband-name { font-size: 8pt; font-weight: bold; }
      .wristband-sub { font-size: 6pt; color: #555; }
    </style></head><body>
    <div class="wristband">
      ${content.querySelector('.qr-svg-wrist').outerHTML}
      <div class="wristband-text">
        <div class="wristband-name">${e(ospite.cognome)} ${e(ospite.nome)}</div>
        <div class="wristband-sub">Cam. ${e(ospite.stanza || '-')}/${e(ospite.letto || '-')} ${ospite.codice_fiscale ? '| CF: ' + e(ospite.codice_fiscale.substring(0, 6)) + '...' : ''}</div>
      </div>
    </div>
    <script>setTimeout(()=>{window.print();window.close()},400)</script></body></html>`, 'width=400,height=300');
  };

  return (
    <>
      <Button
        variant="outline"
        size={size}
        onClick={() => setShowDialog(true)}
        data-testid="qr-code-btn"
        title="QR Code / Etichetta"
      >
        <QrCode className="w-4 h-4 mr-1" />QR
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-brand" />
              QR Code Ospite
            </DialogTitle>
          </DialogHeader>
          <div ref={printRef} className="space-y-4">
            {/* Label preview */}
            <div className="border rounded-xl p-6 flex flex-col items-center gap-3 bg-white">
              <p className="text-[10px] text-slate-400 uppercase tracking-widest">RSA S. Maria Valleverde</p>
              <div className="qr-svg-print">
                <QRCodeSVG value={qrData} size={160} level="M" includeMargin={false} />
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-slate-900">{ospite.cognome} {ospite.nome}</p>
                <p className="text-sm text-slate-500">
                  Camera {ospite.stanza || '-'} / Letto {ospite.letto || '-'}
                  {ospite.area && <Badge variant="outline" className="text-[9px] ml-2 capitalize">{ospite.area}</Badge>}
                </p>
                {ospite.data_nascita && (
                  <p className="text-xs text-slate-400 mt-1">
                    Nato/a: {new Date(ospite.data_nascita).toLocaleDateString('it-IT')}
                  </p>
                )}
              </div>
              <p className="text-[9px] text-slate-300 font-mono">ID: {ospite.id.substring(0, 8)}</p>
            </div>

            {/* Wristband preview */}
            <div className="border rounded-lg p-3 flex items-center gap-3 bg-slate-50">
              <div className="qr-svg-wrist flex-shrink-0">
                <QRCodeSVG value={qrData} size={48} level="L" includeMargin={false} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">{ospite.cognome} {ospite.nome}</p>
                <p className="text-[10px] text-slate-500">Cam. {ospite.stanza || '-'}/{ospite.letto || '-'} {ospite.codice_fiscale ? `| CF: ${ospite.codice_fiscale.substring(0, 6)}...` : ''}</p>
              </div>
              <Badge variant="outline" className="text-[8px]">Braccialetto</Badge>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Chiudi</Button>
              <Button onClick={handlePrintBraccialetto} variant="outline" className="border-slate-300 text-slate-700" data-testid="print-wristband-btn">
                <Printer className="w-4 h-4 mr-1" />Braccialetto
              </Button>
              <Button onClick={handlePrintQR} className="bg-brand hover:bg-brand-dark text-white" data-testid="print-qr-btn">
                <Printer className="w-4 h-4 mr-1" />Etichetta QR
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
